export type AnalyticsPrimitive = string | number | boolean | null;
export type AnalyticsPropertyValue = AnalyticsPrimitive | AnalyticsPrimitive[];
export type AnalyticsProperties = Record<string, unknown>;

const BLOCKED_KEY_PATTERN =
  /email|token|password|secret|note|description|body|message|stack|invite/i;

/**
 * Backstop for health data. Call sites are written to send only generic properties, but any
 * key that names a fertility/health concept is dropped here too, so a future event can't
 * quietly ship "hasBleeding" or a tracking goal to PostHog. Matches keys only (never values),
 * and none of it overlaps identity keys (id, email, name) set by `identify`.
 */
const HEALTH_KEY_PATTERN =
  /goal|ttc|avoid|pregnan|postpartum|breastfe|bleed|intercourse|mucus|temperature|bbt|symptom|peak|fertil|ovulat|opk|cycle_?day|cycle_?start|observation_?date|tracking_?method|optional_?field/i;

export const isBlockedAnalyticsKey = (key: string): boolean =>
  BLOCKED_KEY_PATTERN.test(key) || HEALTH_KEY_PATTERN.test(key);
const MAX_STRING_LENGTH = 120;

const sanitizeString = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.slice(0, MAX_STRING_LENGTH);
};

const sanitizePrimitive = (value: unknown): AnalyticsPrimitive | undefined => {
  if (typeof value === "string") {
    return sanitizeString(value);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return undefined;
};

const sanitizeArray = (value: unknown[]): AnalyticsPrimitive[] | undefined => {
  const sanitized = value
    .map((entry) => sanitizePrimitive(entry))
    .filter((entry): entry is AnalyticsPrimitive => entry !== undefined);

  return sanitized.length > 0 ? sanitized : undefined;
};

export interface SanitizeAnalyticsOptions {
  /**
   * Keys allowed through even though they match the blocked pattern. Only for values we
   * deliberately want in PostHog — identity traits like `verified_email` read as PII by the
   * pattern but are the whole point of a person profile.
   */
  allowKeys?: readonly string[];
}

export const sanitizeAnalyticsProperties = (
  input: AnalyticsProperties = {},
  options: SanitizeAnalyticsOptions = {}
): Record<string, AnalyticsPropertyValue> => {
  const sanitized: Record<string, AnalyticsPropertyValue> = {};
  const allowedKeys = new Set(options.allowKeys ?? []);

  for (const [rawKey, rawValue] of Object.entries(input)) {
    const trimmedKey = rawKey.trim();
    const isRequiredVendorKey = trimmedKey === "token" || allowedKeys.has(trimmedKey);

    if (!trimmedKey || (!isRequiredVendorKey && isBlockedAnalyticsKey(trimmedKey))) {
      continue;
    }

    const value = Array.isArray(rawValue)
      ? sanitizeArray(rawValue)
      : sanitizePrimitive(rawValue);

    if (value !== undefined) {
      sanitized[trimmedKey] = value;
    }
  }

  return sanitized;
};

const MAX_LOG_BODY_LENGTH = 2000;

const LOG_REDACTIONS: Array<[RegExp, string]> = [
  // Emails, JWTs and bearer/api tokens are the values most likely to ride along in a
  // console call that was only ever meant for a local devtools window.
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]"],
  [/eyJ[\w-]{6,}\.[\w-]{6,}\.[\w-]{6,}/g, "[jwt]"],
  // Underscores allowed mid-key so prefixed forms like sk_live_… match, not just sk_….
  [/\b(?:sk|pk|phc|rk|whsec)_[A-Za-z0-9_]{8,}/g, "[key]"],
  [/(bearer\s+)[A-Za-z0-9._-]{8,}/gi, "$1[token]"],
];

/**
 * Console output is written for developers, not for an analytics pipeline, so anything
 * forwarded to PostHog's logs product gets scrubbed of the credential/identity shapes we
 * know can appear in it and capped in size.
 */
export const redactLogBody = (value: string): string => {
  const redacted = LOG_REDACTIONS.reduce(
    (body, [pattern, replacement]) => body.replace(pattern, replacement),
    value
  );

  return redacted.length > MAX_LOG_BODY_LENGTH
    ? `${redacted.slice(0, MAX_LOG_BODY_LENGTH)}…[truncated]`
    : redacted;
};

// Path segments that identify a person or a secret: UUIDs (any variant, with hyphens) and
// 32-hex tokens (the dashless UUIDs we mint for invite links). Instructor routes carry the
// client's user id in the path, and invite links carry the token, so a raw pathname would
// hand both to PostHog/Sentry.
const UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_TOKEN_SEGMENT_PATTERN = /^[0-9a-f]{32}$/i;

const isIdentifierSegment = (segment: string): boolean =>
  UUID_SEGMENT_PATTERN.test(segment) || HEX_TOKEN_SEGMENT_PATTERN.test(segment);

export const sanitizePath = (value: string): string => {
  const [withoutHash] = value.split("#", 1);
  const [withoutQuery] = withoutHash.split("?", 1);
  if (!withoutQuery) {
    return "/";
  }

  return withoutQuery
    .split("/")
    .map((segment) => (isIdentifierSegment(segment) ? ":id" : segment))
    .join("/");
};

export const sanitizeUrl = (value: string): string => {
  try {
    const parsed = new URL(value, window.location.origin);
    return `${parsed.origin}${sanitizePath(parsed.pathname)}`;
  } catch {
    return sanitizePath(value);
  }
};

// PostHog's own ($-prefixed) properties that carry a full URL or a pathname. `$pageview` /
// `$pageleave` (and the $set/$set_once initial-* person props riding on them) would otherwise
// ship `/instructor/clients/<client uuid>` and `/invite/<token>` verbatim.
const POSTHOG_URL_KEYS = new Set([
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_referrer",
  "$prev_pageview_url",
]);
const POSTHOG_PATH_KEYS = new Set([
  "$pathname",
  "$prev_pageview_pathname",
  "$initial_pathname",
  "$session_entry_pathname",
]);

const sanitizeUrlLike = (value: string): string => {
  // Leave PostHog sentinels like "$direct" alone; only rewrite actual URLs / paths.
  if (value.startsWith("/")) return sanitizePath(value);
  return /^https?:\/\//i.test(value) ? sanitizeUrl(value) : value;
};

const sanitizePathLike = (value: string): string =>
  value.startsWith("/") ? sanitizePath(value) : value;

const sanitizePostHogUrlFields = (input: AnalyticsProperties): AnalyticsProperties => {
  const output: AnalyticsProperties = { ...input };
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === "string") {
      if (POSTHOG_URL_KEYS.has(key)) output[key] = sanitizeUrlLike(value);
      else if (POSTHOG_PATH_KEYS.has(key)) output[key] = sanitizePathLike(value);
    }
  }
  return output;
};

/**
 * PostHog's own events keep their vendor properties (they drive the product's built-in
 * features), but every URL/path in them is reduced to origin + id-redacted path, including the
 * copies inside `$set` / `$set_once`.
 */
export const sanitizePostHogVendorProperties = (
  properties: AnalyticsProperties = {}
): AnalyticsProperties => {
  const sanitized = sanitizePostHogUrlFields(properties);
  for (const nestedKey of ["$set", "$set_once"]) {
    const nested = sanitized[nestedKey];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      sanitized[nestedKey] = sanitizePostHogUrlFields(nested as AnalyticsProperties);
    }
  }
  return sanitized;
};

const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>]+/gi;

/** Rewrites every URL inside free text (e.g. an http span description "GET https://…?user_id=…"). */
export const sanitizeUrlsInText = (value: string): string =>
  value.replace(URL_IN_TEXT_PATTERN, (url) => sanitizeUrl(url));

type SentryBreadcrumbLike = {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
};

/** Navigation breadcrumbs carry from/to paths; fetch/xhr ones carry the request URL. */
export const sanitizeSentryBreadcrumb = <T extends SentryBreadcrumbLike>(breadcrumb: T): T => {
  if (!breadcrumb.data) {
    return breadcrumb;
  }

  const data = { ...breadcrumb.data };
  for (const key of ["from", "to"]) {
    if (typeof data[key] === "string") data[key] = sanitizeUrlLike(data[key] as string);
  }
  if (typeof data.url === "string") data.url = sanitizeUrlLike(data.url);

  return { ...breadcrumb, data };
};

type SentryTransactionLike = {
  transaction?: string;
  request?: { url?: string };
  spans?: Array<{ description?: string; data?: Record<string, unknown> }>;
};

/**
 * Transaction names default to the raw pathname (no router instrumentation), and http spans carry
 * full Supabase request URLs whose query strings include user ids.
 */
export const sanitizeSentryTransaction = <T extends SentryTransactionLike>(event: T): T => {
  const next = { ...event };
  if (typeof next.transaction === "string") {
    next.transaction = next.transaction.startsWith("/")
      ? sanitizePath(next.transaction)
      : sanitizeUrlsInText(next.transaction);
  }
  if (next.request?.url) {
    next.request = { ...next.request, url: sanitizeUrl(next.request.url) };
  }
  if (Array.isArray(next.spans)) {
    next.spans = next.spans.map((span) => {
      const sanitizedSpan = { ...span };
      if (typeof span.description === "string") {
        sanitizedSpan.description = sanitizeUrlsInText(span.description);
      }
      if (span.data) {
        const data = { ...span.data };
        delete data["http.query"];
        delete data["http.fragment"];
        for (const key of ["url", "http.url"]) {
          if (typeof data[key] === "string") data[key] = sanitizeUrlLike(data[key] as string);
        }
        sanitizedSpan.data = data;
      }
      return sanitizedSpan;
    });
  }
  return next;
};
